import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, Collapse, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';

import { colors } from '../theme';

interface CollapsibleSectionProps {
  /** Bold section name shown in the header. */
  title: string;
  /** One-line description of what the section shows. */
  description?: string;
  /** Small uppercase category eyebrow (ties the section to a dashboard group). */
  category?: string;
  /** Accent color for the icon chip, eyebrow, and chevron. */
  accent?: string;
  /** Icon rendered inside the tinted chip on the left. */
  icon?: ReactNode;
  /** Optional control rendered on the right of the header, before the
   *  chevron — stays visible whether the section is open or collapsed.
   *  Its own click handlers must stopPropagation to avoid toggling. */
  headerAction?: ReactNode;
  /** Whether the section starts expanded. Defaults to collapsed. */
  defaultExpanded?: boolean;
  children: ReactNode;
}

/**
 * Collapsible section shell for the dashboard. Sections start collapsed
 * so the page opens as a clean, scannable list of named sections the
 * user expands on demand — instead of one long always-expanded scroll.
 *
 * Content is unmounted while collapsed (`unmountOnExit`), so a collapsed
 * section renders nothing and fires no data fetches until it is opened —
 * the page loads faster and only pulls what the user actually looks at.
 *
 * Visual language: a tinted icon chip anchors each row, the chevron sits
 * in a subtle circular target on the right, and the card lifts on hover
 * with a soft layered shadow — the Linear / Stripe / Vercel accordion feel.
 */
export function CollapsibleSection({
  title,
  description,
  category,
  accent = colors.chartPrimary,
  icon,
  headerAction,
  defaultExpanded = false,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultExpanded);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${open ? `${accent}40` : colors.border}`,
        bgcolor: '#fff',
        overflow: 'hidden',
        boxShadow: open
          ? `0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px ${accent}55`
          : '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease',
        '&:hover': {
          borderColor: `${accent}66`,
          boxShadow: `0 1px 2px rgba(15,23,42,0.04), 0 10px 28px -14px ${accent}66`,
          transform: open ? 'none' : 'translateY(-1px)',
        },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.25, sm: 1.75 },
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 1.75 },
          cursor: 'pointer',
          userSelect: 'none',
          '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: -2 },
        }}
      >
        {/* Tinted icon chip — the color anchor for the row. */}
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: 34, sm: 40 },
            height: { xs: 34, sm: 40 },
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accent,
            bgcolor: `${accent}14`,
            border: `1px solid ${accent}22`,
            transition: 'background-color 160ms ease',
            '& svg': { fontSize: { xs: 18, sm: 21 } },
          }}
        >
          {icon ?? <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent }} />}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {category && (
            <Typography
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.13em',
                textTransform: 'uppercase',
                color: accent,
                mb: 0.15,
              }}
            >
              {category}
            </Typography>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 14, sm: 15 },
              color: colors.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              sx={{
                fontSize: { xs: 11.5, sm: 12.5 },
                color: colors.textMuted,
                lineHeight: 1.4,
                mt: 0.15,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {headerAction && <Box sx={{ flexShrink: 0 }}>{headerAction}</Box>}

        {/* Chevron in a subtle circular target on the right. */}
        <Box
          sx={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: open ? accent : colors.textMuted,
            bgcolor: open ? `${accent}14` : 'transparent',
            transition: 'background-color 160ms ease, color 160ms ease',
          }}
        >
          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: 22,
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </Box>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ px: { xs: 0.75, sm: 1.5 }, pb: { xs: 0.75, sm: 1.5 }, pt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}
